import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from "react";
import { X } from "lucide-react";
import { setStepUpProvider, type StepUpProblem } from "../../api/client";
import { useAuth } from "../../auth/AuthContext";
import { SiteEmblem } from "../brand";
import { StepUpChallenge } from "./StepUpChallenge";

/**
 * Mounted once at the root (ported from Summit Engineering's MfaProvider). When the API answers 428 "confirm it's you" —
 * the sign-in of a privileged role (Q-O12) has not passed the authenticator step (first visit, a new Microsoft sign-in, or
 * an Admin reset) — the API client asks this provider; it shows "Confirm it's you" over the page and the refused request
 * is sent again once the API has verified the code.
 */
export function StepUpProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const [open, setOpen] = useState(false);
  const [freshCode, setFreshCode] = useState(false);
  const titleId = useId();
  const returnFocus = useRef<Element | null>(null);
  // One prompt for every request refused at the same time: they all wait on the same answer and are all resent.
  const inFlight = useRef<{ promise: Promise<boolean>; resolve: (ok: boolean) => void } | null>(null);
  const refresh = auth.refresh;

  const ask = useCallback((problem?: StepUpProblem) => {
    // O-R90: an hour without signing — the sign-in is still verified, but signing needs a new code.
    if (inFlight.current) {
      if (problem?.signingIdle === true) setFreshCode(true);
      return inFlight.current.promise;
    }
    returnFocus.current = document.activeElement;
    setFreshCode(problem?.signingIdle === true);
    let resolve: (ok: boolean) => void = () => undefined;
    const promise = new Promise<boolean>((r) => {
      resolve = r;
    });
    inFlight.current = { promise, resolve };
    setOpen(true);
    return promise;
  }, []);

  useEffect(() => {
    setStepUpProvider(ask);
    return () => setStepUpProvider(null);
  }, [ask]);

  const finish = (ok: boolean) => {
    const current = inFlight.current;
    inFlight.current = null;
    setOpen(false);
    current?.resolve(ok);
    if (ok) void refresh();
    (returnFocus.current as HTMLElement | null)?.focus?.();
  };

  return (
    <>
      {children}
      {open && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/40 p-4" role="presentation">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            className="w-full max-w-md rounded-xl border border-slate-200 border-t-4 border-t-brand-900 bg-white p-5 shadow-xl animate-fade-in"
            data-testid="step-up-dialog"
            onKeyDown={(e) => {
              if (e.key === "Escape") finish(false);
            }}
          >
            <div className="mb-3 flex items-start gap-3">
              <SiteEmblem size={36} alt="" className="h-9 w-9 flex-none rounded-full bg-brand-50" testId="step-up-emblem" />
              <div className="min-w-0 flex-1">
                <h2 id={titleId} className="text-base font-semibold text-slate-900">
                  Confirm it&apos;s you
                </h2>
                <p className="mt-0.5 text-sm text-slate-600">Summit Operations · access for roles that approve, publish or administer</p>
              </div>
              <button
                type="button"
                className="rounded p-1 text-slate-500 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                onClick={() => finish(false)}
              >
                <X className="h-4 w-4" aria-hidden="true" />
                <span className="sr-only">Cancel</span>
              </button>
            </div>
            <StepUpChallenge onVerified={() => finish(true)} requireFreshCode={freshCode} />
          </div>
        </div>
      )}
    </>
  );
}
